/**
 * @file index.jsx
 *
 * Client-side entry point. Mounts the React app into the DOM, configures
 * third-party providers (Google OAuth, React Router), registers all
 * application routes, and initializes Crisp chat and PostHog analytics.
 */
import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { Crisp } from "crisp-sdk-web";
import posthog from "posthog-js";
import { ToastContainer } from "react-toastify";

// Initialize Sentry error tracking (only if DSN is configured)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
  });
}

// Initialize Crisp live-chat widget (Spanish locale)
const crispId = import.meta.env.VITE_CRISP_ID;
if (crispId) {
  Crisp.configure(crispId, { locale: "es" });
  // Push Crisp bubble above the footer once it loads
  const observer = new MutationObserver(() => {
    const bubble = document.querySelector(".cc-1qbp0");
    if (bubble) {
      bubble.style.setProperty("margin-bottom", "64px", "important");
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// Initialize PostHog product analytics (only identifies logged-in users)
const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
    person_profiles: "identified_only",
  });
}
import "react-toastify/dist/ReactToastify.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AppProvider, useAppContext } from "./context/AppContext";

import "./app.css";
import AppShell from "./components/AppShell";
import Footer from "./components/Footer";
import ErrorFallback from "./components/ErrorFallback";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { TooltipProvider } from "./ui";

// Auth pages
import Login from "./pages/Login";
import ProfessionalLogin from "./pages/ProfessionalLogin";
import PatientRegister from "./pages/PatientRegister";
import PatientLogin from "./pages/PatientLogin";
import VerifyCode from "./pages/VerifyCode";
import ProfessionalRegister from "./pages/ProfessionalRegister";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// Consultto pages
import Dashboard from "./pages/Dashboard";
import Professionals from "./pages/Professionals";
import ProfessionalProfile from "./pages/ProfessionalProfile";
import MyProfile from "./pages/MyProfile";
import Patients from "./pages/Patients";
import PatientDetail from "./pages/PatientDetail";
import PatientProfile from "./pages/PatientProfile";
import Appointments from "./pages/Appointments";
import NewAppointment from "./pages/NewAppointment";
import AppointmentDetail from "./pages/AppointmentDetail";
import ScheduleManager from "./pages/ScheduleManager";
import AddLocation from "./pages/AddLocation";
import PublicProfile from "./pages/PublicProfile";
import AdminUsers from "./pages/AdminUsers";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUserDetail from "./pages/AdminUserDetail";
import AdminAppointments from "./pages/AdminAppointments";
import AdminProfessionals from "./pages/AdminProfessionals";
import AdminAuditLog from "./pages/AdminAuditLog";
import AdminNotifications from "./pages/AdminNotifications";
import AdminSettings from "./pages/AdminSettings";
import AdminReferrals from "./pages/AdminReferrals";
import AdminLayout from "./components/AdminLayout";
import ImpersonationBanner from "./components/ImpersonationBanner";
import TodayView from "./pages/TodayView";
import CalendarView from "./pages/CalendarView";
import AppointmentTokenDetail from "./pages/AppointmentTokenDetail";
import Onboarding from "./pages/Onboarding";
import Configuracion from "./pages/Configuracion";
import AcceptTerms from "./pages/AcceptTerms";
import ProfessionalRoute from "./components/ProfessionalRoute";
import PatientLayout from "./components/PatientLayout";
import PatientHome from "./pages/PatientHome";
import PatientAppointments from "./pages/PatientAppointments";
import PatientProfessionals from "./pages/PatientProfessionals";
import Billing from "./pages/Billing";
import EmailGatePage from "./pages/EmailGatePage";

// Boilerplate pages
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

/** Inner app wrapped by AppProvider so it can read isDarkMode from context */
const ThemedApp = () => {
  const { isDarkMode } = useAppContext();

  // Toggle data-theme on <html> so tokens in src/client/styles/tokens.css resolve
  // to the right (light|dark) values, plus native color-scheme for form controls.
  React.useEffect(() => {
    document.documentElement.style.colorScheme = isDarkMode ? "dark" : "light";
    document.documentElement.setAttribute("data-theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ""}>
      <BrowserRouter>
        <AuthProvider>
          {/* Radix tooltips (e.g. admin notification log) need one provider near
              the root, otherwise <Tooltip> throws and the page crashes. */}
          <TooltipProvider>
          <ImpersonationBanner />
          <AppShell>
            <Routes>
              {/* Public auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/login/profesional" element={<ProfessionalLogin />} />
              <Route path="/login/paciente" element={<PatientLogin />} />
              <Route path="/patient/login" element={<PatientLogin />} /> {/* Legacy URL */}
              <Route path="/register/patient" element={<PatientRegister />} />
              <Route path="/verify-code" element={<VerifyCode />} />
              <Route path="/register/professional" element={<ProfessionalRegister />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Dashboard (replaces Home as default) */}
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

              {/* Professional routes — /profesionales also used by patients (wrapped in PatientLayout below via /profesionales) */}
              <Route path="/profesionales" element={<ProtectedRoute><Professionals /></ProtectedRoute>} />
              <Route path="/profesional/:id" element={<ProtectedRoute><ProfessionalProfile /></ProtectedRoute>} />
              <Route path="/aceptar-terminos" element={<ProtectedRoute><AcceptTerms /></ProtectedRoute>} />
              <Route path="/onboarding" element={<ProtectedRoute roles={["PROFESIONAL"]}><Onboarding /></ProtectedRoute>} />
              <Route path="/mi-perfil" element={<ProfessionalRoute><MyProfile /></ProfessionalRoute>} />
              <Route path="/mi-perfil/ubicacion" element={<ProfessionalRoute><AddLocation /></ProfessionalRoute>} />
              <Route path="/configuracion" element={<ProfessionalRoute><Configuracion /></ProfessionalRoute>} />
              <Route path="/mi-plan" element={<ProfessionalRoute><Billing /></ProfessionalRoute>} />

              {/* Patient portal routes */}
              <Route path="/mi-salud" element={<ProtectedRoute roles={["PACIENTE"]}><PatientLayout><PatientHome /></PatientLayout></ProtectedRoute>} />
              <Route path="/mis-citas" element={<ProtectedRoute roles={["PACIENTE"]}><PatientLayout><PatientAppointments /></PatientLayout></ProtectedRoute>} />
              <Route path="/mis-profesionales" element={<ProtectedRoute roles={["PACIENTE"]}><PatientLayout><PatientProfessionals /></PatientLayout></ProtectedRoute>} />
              <Route path="/mi-perfil-paciente" element={<ProtectedRoute roles={["PACIENTE"]}><PatientLayout><PatientProfile /></PatientLayout></ProtectedRoute>} />

              {/* Patient management routes (for professionals/admins) */}
              <Route path="/pacientes" element={<ProtectedRoute roles={["PROFESIONAL", "ADMIN"]}><Patients /></ProtectedRoute>} />
              <Route path="/paciente/:id" element={<ProtectedRoute roles={["PROFESIONAL", "ADMIN"]}><PatientDetail /></ProtectedRoute>} />

              {/* Appointment routes */}
              <Route path="/citas" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
              <Route path="/nueva-cita" element={<ProtectedRoute><NewAppointment /></ProtectedRoute>} />
              <Route path="/cita/:id" element={<ProtectedRoute><AppointmentDetail /></ProtectedRoute>} />

              {/* Schedule */}
              <Route path="/mi-horario" element={<ProfessionalRoute><ScheduleManager /></ProfessionalRoute>} />

              {/* Calendar + today's view */}
              <Route path="/calendario" element={<ProtectedRoute><CalendarView /></ProtectedRoute>} />
              <Route path="/hoy" element={<ProfessionalRoute><TodayView /></ProfessionalRoute>} />

              {/* Admin panel — obscured under /cx/ */}
              <Route path="/cx" element={<ProtectedRoute roles={["ADMIN"]}><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
              <Route path="/cx/usuarios" element={<ProtectedRoute roles={["ADMIN"]}><AdminLayout><AdminUsers /></AdminLayout></ProtectedRoute>} />
              <Route path="/cx/usuario/:id" element={<ProtectedRoute roles={["ADMIN"]}><AdminLayout><AdminUserDetail /></AdminLayout></ProtectedRoute>} />
              <Route path="/cx/citas" element={<ProtectedRoute roles={["ADMIN"]}><AdminLayout><AdminAppointments /></AdminLayout></ProtectedRoute>} />
              <Route path="/cx/profesionales" element={<ProtectedRoute roles={["ADMIN"]}><AdminLayout><AdminProfessionals /></AdminLayout></ProtectedRoute>} />
              <Route path="/cx/auditoria" element={<ProtectedRoute roles={["ADMIN"]}><AdminLayout><AdminAuditLog /></AdminLayout></ProtectedRoute>} />
              <Route path="/cx/notificaciones" element={<ProtectedRoute roles={["ADMIN"]}><AdminLayout><AdminNotifications /></AdminLayout></ProtectedRoute>} />
              <Route path="/cx/configuracion" element={<ProtectedRoute roles={["ADMIN"]}><AdminLayout><AdminSettings /></AdminLayout></ProtectedRoute>} />
              <Route path="/cx/referidos" element={<ProtectedRoute roles={["ADMIN"]}><AdminLayout><AdminReferrals /></AdminLayout></ProtectedRoute>} />

              {/* Email verification gate (phone-registered patients) */}
              <Route path="/verificar-email" element={<EmailGatePage />} />

              {/* Public pages — no auth required */}
              <Route path="/c/:slug" element={<PublicProfile />} />
              <Route path="/cita-detalle/:token" element={<AppointmentTokenDetail />} />
              <Route path="/mi-salud/citas/:token" element={<AppointmentTokenDetail />} />
              <Route path="/cita/confirmar/:token" element={<AppointmentTokenDetail />} />
              <Route path="/cita/gestionar/:token" element={<AppointmentTokenDetail />} />

              {/* Boilerplate routes */}
              <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppShell>
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
};

const root = document.getElementById("root");
if (root !== null) {
  const appRoot = createRoot(root);
  appRoot.render(
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <HelmetProvider>
      <ToastContainer position="bottom-right" theme="dark" />
      <AppProvider>
        <ThemedApp />
      </AppProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}
